// src/routes/core/withRouteAnalytics.tsx
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { recordToolUsage } from '@/services/toolUsage/toolUsageService';

const analytics = {
  logPageView: (pageTitle: string, path: string, timing?: number) => {
    console.log(`[Analytics] Page view: ${pageTitle} at ${path}`, { timing });
  },
  startTiming: (label: string) => performance.now(),
  endTiming: (label: string, startTime: number, pageTitle: string, path: string) => {
    const duration = performance.now() - startTime;
    analytics.logPageView(pageTitle, path, duration);
  },
};

// Opt-in tool-usage tracking metadata, passed through from a route's
// `meta.toolId` (see createProtectedRoute.tsx and src/routes/core/types.ts).
// Undefined for the ~400 tool routes that haven't been annotated yet — this
// wrapper is otherwise a no-op for tracking, same as before this field
// existed. See the honesty boundary comment in
// src/services/toolUsage/toolUsageService.ts: this must stay read-only
// discovery data, never wired into any score.
export interface RouteToolMeta {
  toolId: string;
  toolName: string;
  country?: string;
  category?: string;
}

export function withRouteAnalytics(
  Component: React.ReactNode,
  pageTitle: string,
  toolMeta?: RouteToolMeta,
): React.FC {
  return function AnalyticsWrapper() {
    const location = useLocation();
    const { user } = useAuth();
    const uid = user?.id;

    useEffect(() => {
      const start = analytics.startTiming(pageTitle);
      analytics.logPageView(pageTitle, location.pathname);
      return () => {
        analytics.endTiming(pageTitle, start, pageTitle, location.pathname);
      };
    }, [location.pathname, pageTitle]);

    useEffect(() => {
      if (!toolMeta || !uid) return;
      recordToolUsage({
        toolId: toolMeta.toolId,
        toolName: toolMeta.toolName,
        toolPath: location.pathname,
        country: toolMeta.country,
        category: toolMeta.category,
      }).catch((err) => console.error('[withRouteAnalytics] tool usage tracking failed', err));
      // Fire once per mount for this route + user, not on every pathname
      // change within the same mounted component (matters for routes that
      // reuse one element across params).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, toolMeta?.toolId]);

    return <>{Component}</>;
  };
}